"use client";
import React from "react";
import { Box, Container, Typography } from "@mui/material";
import Image from "next/image";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { useTranslation } from "react-i18next";

const SAGE_PINK = "#E0457B";

const SageCapabilitiesSection = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const items = t("sageCapabilities.items", { returnObjects: true });
  const cards = Array.isArray(items) ? items : [];

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 6, sm: 8, md: 10 },
        backgroundColor: "#F7F3FF",
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 5 } }}>
          <Typography
            sx={{
              fontSize: { xs: "10px", sm: "11px" },
              fontWeight: 600,
              color: SAGE_PINK,
              letterSpacing: "1.5px",
              textTransform: "uppercase",
              fontFamily: "var(--font-outfit)",
              mb: { xs: 1.5, sm: 2 },
            }}
          >
            {t("sageCapabilities.label")}
          </Typography>

          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "26px", sm: "30px", md: "34px", lg: "38px" },
              fontWeight: 700,
              lineHeight: 1.25,
              color: "#14122B",
              fontFamily: "var(--font-plusjakarta)",
              mb: { xs: 2, sm: 2.5 },
            }}
          >
            {t("sageCapabilities.heading")}
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "14px", sm: "15px", md: "15px" },
              color: "#55556E",
              lineHeight: 1.8,
              fontWeight: 400,
              fontFamily: "var(--font-outfit)",
              maxWidth: 720,
              mx: "auto",
            }}
          >
            {t("sageCapabilities.description")}
          </Typography>
        </Box>

        {/* Cards grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
            gap: { xs: 2, md: 2.5 },
            mb: { xs: 5, md: 7 },
          }}
        >
          {cards.map((text, index) => (
            <Box
              key={index}
              sx={{
                backgroundColor: "#FFFFFF",
                border: "1px solid #F3E4EC",
                borderRadius: "14px",
                p: { xs: 2.5, md: 3 },
                display: "flex",
                flexDirection: "column",
                gap: 2,
                boxShadow: "0 2px 12px rgba(229,72,127,0.04)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: "0 10px 28px rgba(229,72,127,0.12)",
                },
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "6px",
                  backgroundColor: "#FDEBF2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <CheckRoundedIcon
                  sx={{
                    fontSize: 15,
                    color: SAGE_PINK,
                    stroke: SAGE_PINK,
                    strokeWidth: 1.1,
                  }}
                />
              </Box>

              <Typography
                sx={{
                  fontSize: { xs: "13px", sm: "14px", md: "14.5px" },
                  color: "#2E2B4A",
                  lineHeight: 1.7,
                  fontWeight: 400,
                  fontFamily: "var(--font-outfit)",
                  textAlign: isArabic ? "right" : "left",
                }}
              >
                {text}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Dashboard image */}
        <Box
          sx={{
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              aspectRatio: "16 / 9",
              borderRadius: { xs: "10px", md: "14px" },
              overflow: "hidden",
            }}
          >
            <Image
              src="https://getgeek-website-assets.b-cdn.net/SageFluff.webp"
              alt="Sage AI interview copilot dashboard during a live interview"
              fill
              sizes="(max-width: 1200px) 100vw, 1200px"
              style={{ objectFit: "contain" }}
            />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default SageCapabilitiesSection;